/**
 * Order Status Chart Component
 * Displays order count by status as horizontal bars
 */

import React from 'react';
import { OrderStatusData } from '../../types';

interface OrderStatusChartProps {
  data: OrderStatusData[];
}

const STATUS_COLORS: Record<string, string> = {
  'PENDING': 'bg-yellow-500',
  'pending': 'bg-yellow-500',
  'CONFIRMED': 'bg-blue-500',
  'confirmed': 'bg-blue-500',
  'PROCESSING': 'bg-purple-500',
  'processing': 'bg-purple-500',
  'DELIVERED': 'bg-green-500',
  'delivered': 'bg-green-500',
  'CANCELLED': 'bg-red-500',
  'cancelled': 'bg-red-500',
  'IN_PROGRESS': 'bg-indigo-500',
  'in_progress': 'bg-indigo-500',
  'READY': 'bg-teal-500',
  'ready': 'bg-teal-500',
  'DISPATCHED': 'bg-orange-500',
  'dispatched': 'bg-orange-500'
};

export const OrderStatusChart: React.FC<OrderStatusChartProps> = ({ data }) => {
  if (!data || data.length === 0) {
    return (
      <div className="flex items-center justify-center h-48 text-gray-400">
        No order data available
      </div>
    );
  }

  const maxCount = Math.max(...data.map(d => d.count), 1);

  return (
    <div className="space-y-4">
      {/* Legend with color indicators */}
      <div className="p-3 bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg border border-gray-200">
        <div className="text-xs font-medium text-gray-700 mb-3">Order Status Distribution</div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {data.map((item) => {
            const displayStatus = item.status.charAt(0).toUpperCase() + item.status.slice(1).toLowerCase();
            const colorClass = STATUS_COLORS[item.status] || 'bg-gray-500';
            return (
              <div key={item.status} className="flex items-center gap-2">
                <div className={`w-3 h-3 rounded-full ${colorClass} shadow-sm`}></div>
                <span className="text-xs text-gray-600">{displayStatus}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Status bars */}
      <div className="space-y-3">
        {data.map((item) => {
          const percentage = (item.count / maxCount) * 100;
          const displayStatus = item.status.charAt(0).toUpperCase() + item.status.slice(1).toLowerCase();
          const colorClass = STATUS_COLORS[item.status] || 'bg-gray-500';

          return (
            <div key={item.status} className="space-y-1">
              {/* Label with count */}
              <div className="flex justify-between items-center">
                <span className="text-xs font-medium text-gray-700">{displayStatus}</span>
                <span className="text-xs font-semibold text-gray-900">{item.count} orders</span>
              </div>

              {/* Bar */}
              <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                <div
                  className={`h-3 rounded-full transition-all duration-300 ${colorClass} shadow-sm`}
                  style={{ width: `${Math.max(percentage, 5)}%` }}
                  title={`${displayStatus}: ${item.count} orders • ₹${(item.totalValue / 1000).toFixed(1)}K`}
                />
              </div>

              {/* Metadata */}
              <div className="text-xs text-gray-500">
                Revenue: ₹{(item.totalValue / 1000).toFixed(1)}K • {((item.count / data.reduce((sum, d) => sum + d.count, 0)) * 100).toFixed(1)}%
              </div>
            </div>
          );
        })}
      </div>

      {/* Summary metrics */}
      <div className="grid grid-cols-2 gap-3">
        {/* Total Orders */}
        <div className="p-3 bg-gradient-to-br from-blue-50 to-gray-50 rounded-lg border border-blue-100">
          <div className="text-xs text-gray-600 font-medium">Total Orders</div>
          <div className="text-lg font-bold text-blue-600 mt-1">
            {data.reduce((sum, d) => sum + d.count, 0)}
          </div>
          <div className="text-xs text-gray-500 mt-1">All statuses</div>
        </div>

        {/* Total Value */}
        <div className="p-3 bg-gradient-to-br from-green-50 to-gray-50 rounded-lg border border-green-100">
          <div className="text-xs text-gray-600 font-medium">Total Value</div>
          <div className="text-lg font-bold text-green-600 mt-1">
            ₹{(data.reduce((sum, d) => sum + d.totalValue, 0) / 1000000).toFixed(2)}M
          </div>
          <div className="text-xs text-gray-500 mt-1">All orders</div>
        </div>
      </div>
    </div>
  );
};

export default OrderStatusChart;
